import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, LogOut, RefreshCcw, Inbox, Clock, CheckCircle2, XCircle, Lock,
  Users, UserPlus, Trash2, Mail, FileCheck, FileText, ShieldCheck, ExternalLink,
  Upload, Paperclip, X, Image as ImageIcon, Download, Search, Pencil, Eye, EyeOff,
} from "lucide-react";

// Build API base robustly so `REACT_APP_BACKEND_URL` can include or omit the trailing `/api`.
const _RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const _BACKEND_BASE = _RAW_BACKEND.replace(/\/+$/, "");
const API = _BACKEND_BASE.endsWith("/api") ? _BACKEND_BASE : `${_BACKEND_BASE}/api`;
const TOKEN_KEY = "ik_auth_token";
const USER_KEY = "ik_auth_user";
const OWNER_EMAIL = "infokarta@infokarta.hr"; // financijska polja vidi samo vlasnik

const STATUSES = [
  { value: "novo", label: "Novo", tone: "bg-[#0A192F] text-white" },
  { value: "u_obradi", label: "U obradi", tone: "bg-[#F4D35E] text-[#0A192F]" },
  { value: "zavrsen", label: "Završen", tone: "bg-[#15803D] text-white" },
  { value: "predan", label: "Predan", tone: "bg-[#0EA5E9] text-white" },
  { value: "rijeseno", label: "Rješenje izdano", tone: "bg-[#16A34A] text-white" },
  { value: "stranka_odustala", label: "Stranka odustala", tone: "bg-[#6B7280] text-white" },
  { value: "odbijeno", label: "Odbijeno", tone: "bg-[#D92D20] text-white" },
];
const statusMap = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

const TYPES = {
  kuca: "Obiteljska kuća",
  stan: "Stan",
  pomocna_zgrada: "Pomoćna zgrada",
  gospodarska_zgrada: "Gospodarska zgrada",
  drugo: "Drugo",
};

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function matchSearch(a, q) {
  if (!q) return true;
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const haystack = [
    a.full_name, a.email, a.phone,
    a.client_address, a.client_oib,
    a.property_address, a.city, a.cadastral_info,
    a.cadastral_municipality, a.cadastral_parcel,
    a.description, a.note, a.assigned_to,
    a.project_number ? `#${a.project_number}` : "",
    TYPES[a.property_type] || a.property_type,
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(s);
}

function SearchInput({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative w-full sm:w-[320px]">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" strokeWidth={1.5} />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-sm h-10 pl-9 pr-9"
        data-testid={testid}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-soft hover:text-ink"
          aria-label="Izbriši pretragu"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

function formatError(err, fallback = "Došlo je do greške.") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
}

export default function AdminPage() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  });
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ novo: 0, u_obradi: 0, rijeseno: 0, odbijeno: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("prijave"); // prijave | projekti | korisnici
  const isOwner = (user?.role || "").toLowerCase() === "admin" || (user?.email || "").toLowerCase() === OWNER_EMAIL;

  // Non-owners can only see "projekti" tab — default to it
  useEffect(() => {
    if (user && !isOwner && tab !== "projekti") setTab("projekti");
  }, [user, isOwner, tab]);

  const fetchAll = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    setLoading(true);
    try {
      const headers = authHeaders();
      const [list, st] = await Promise.all([
        axios.get(`${API}/applications`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setApps(list.data);
      setStats(st.data);
      console.log("Fetched applications:", list.data, "stats:", st.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        toast.error("Sesija je istekla. Prijavite se ponovno.");
      } else {
        toast.error(formatError(err, "Greška pri dohvaćanju podataka."));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  const handleLoginSuccess = (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    toast.success(`Dobrodošli, ${data.user.full_name}.`);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setApps([]);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/applications/${id}`, { status }, { headers: authHeaders() });
      toast.success("Status ažuriran.");
      fetchAll();
    } catch (err) { toast.error(formatError(err, "Ažuriranje nije uspjelo.")); }
  };

  const saveNote = async (id, note) => {
    try {
      await axios.patch(`${API}/applications/${id}`, { note }, { headers: authHeaders() });
      toast.success("Bilješka spremljena.");
      fetchAll();
    } catch (err) { toast.error(formatError(err, "Spremanje nije uspjelo.")); }
  };

  const markContracted = async (id) => {
    try {
      const { data } = await axios.patch(`${API}/applications/${id}`, { is_contracted: true }, { headers: authHeaders() });
      toast.success(`Projekt ugovoren — dodijeljen broj #${data.project_number}.`);
      setSelected(data);
      fetchAll();
    } catch (err) { toast.error(formatError(err, "Ugovaranje nije uspjelo.")); }
  };

  const assignProjectant = async (id, assigned_to) => {
    try {
      const { data } = await axios.patch(`${API}/applications/${id}`, { assigned_to }, { headers: authHeaders() });
      toast.success(`Zadužen: ${assigned_to}`);
      setSelected(data);
      fetchAll();
    } catch (err) { toast.error(formatError(err, "Dodjela nije uspjela.")); }
  };

  const openDoc = async (id, kind) => {
    try {
      const { data } = await axios.get(`${API}/applications/${id}/${kind}`, {
        headers: authHeaders(),
        responseType: "text",
      });
      const blob = new Blob([data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error(formatError(err, "Dokument se ne može otvoriti."));
    }
  };

  const saveEdit = async (id, payload) => {
    try {
      const { data } = await axios.patch(`${API}/applications/${id}`, payload, { headers: authHeaders() });
      toast.success("Izmjene spremljene.");
      setSelected(data);
      fetchAll();
    } catch (err) {
      toast.error(formatError(err, "Spremanje izmjena nije uspjelo."));
      throw err;
    }
  };

  const openApplication = async (application) => {
    try {
      const { data } = await axios.get(`${API}/applications/${application.id}`, { headers: authHeaders() });
      setSelected(data);
    } catch (err) {
      setSelected(application);
      toast.error(formatError(err, "Greška pri dohvaćanju detalja prijave."));
    }
  };

  if (!user) {
    return <LoginScreen onSuccess={handleLoginSuccess} />;
  }

  return (
    <div data-testid="admin-page">
      <section className="border-b border-hair bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-ink-soft">Admin panel</div>
            <h1 className="font-display text-3xl tracking-tight font-semibold text-ink mt-1">
              {tab === "prijave" ? "Prijave klijenata" : tab === "projekti" ? "Naši projekti" : "Korisnici tima"}
            </h1>
            <div className="text-xs text-ink-soft mt-1.5">
              Prijavljeni kao <span className="text-ink font-medium">{user.full_name}</span> · {user.email}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex border border-hair rounded-sm overflow-hidden">
              {isOwner && (
                <button
                  onClick={() => setTab("prijave")}
                  data-testid="tab-prijave"
                  className={`px-4 py-2 text-sm font-medium ${tab === "prijave" ? "bg-ink text-white" : "bg-white text-ink-soft hover:text-ink"}`}
                >
                  <Inbox className="w-4 h-4 inline mr-1.5 -mt-0.5" strokeWidth={1.5} />Prijave
                </button>
              )}
              <button
                onClick={() => setTab("projekti")}
                data-testid="tab-projekti"
                className={`px-4 py-2 text-sm font-medium ${isOwner ? "border-l border-hair" : ""} ${tab === "projekti" ? "bg-ink text-white" : "bg-white text-ink-soft hover:text-ink"}`}
              >
                <FileCheck className="w-4 h-4 inline mr-1.5 -mt-0.5" strokeWidth={1.5} />Projekti
                {stats.contracted > 0 && (
                  <span className={`ml-2 inline-flex items-center justify-center text-[10px] font-bold rounded-sm px-1.5 ${tab === "projekti" ? "bg-white text-ink" : "bg-[#D92D20] text-white"}`}>
                    {stats.contracted}
                  </span>
                )}
              </button>
              {isOwner && (
                <button
                  onClick={() => setTab("korisnici")}
                  data-testid="tab-korisnici"
                  className={`px-4 py-2 text-sm font-medium border-l border-hair ${tab === "korisnici" ? "bg-ink text-white" : "bg-white text-ink-soft hover:text-ink"}`}
                >
                  <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" strokeWidth={1.5} />Korisnici
                </button>
              )}
            </div>
            {(tab === "prijave" || tab === "projekti") && (
              <button onClick={fetchAll} data-testid="admin-refresh"
                className="inline-flex items-center gap-2 border border-hair text-ink px-4 py-2 text-sm font-medium rounded-sm hover:bg-secondary transition-colors">
                <RefreshCcw className="w-4 h-4" strokeWidth={1.5} /> Osvježi
              </button>
            )}
            <button onClick={logout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Odjava
            </button>
          </div>
        </div>
      </section>

      {tab === "prijave" && (
        <ApplicationsTab
          apps={apps} loading={loading}
          filter={filter} setFilter={setFilter}
          onOpen={openApplication}
        />
      )}
      {tab === "projekti" && (
        <ProjectsTab
          apps={apps.filter((a) => a.is_contracted)}
          loading={loading}
          onOpen={openApplication}
          onOpenDoc={openDoc}
          isOwner={isOwner}
        />
      )}
      {tab === "korisnici" && (
        <UsersTab currentUser={user} />
      )}

      <DetailsDialog
        application={selected}
        onClose={() => setSelected(null)}
        onStatusChange={updateStatus}
        onSaveNote={saveNote}
        onMarkContracted={markContracted}
        onAssign={assignProjectant}
        onOpenDoc={openDoc}
        onSaveEdit={saveEdit}
        isOwner={isOwner}
      />
    </div>
  );
}

/* ----------------- Login screen ----------------- */

function LoginScreen({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setPending(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onSuccess(data);
    } catch (err) {
      toast.error(formatError(err, "Prijava nije uspjela."));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-md border border-hair bg-white p-8 lg:p-10">
        <div className="w-10 h-10 bg-ink flex items-center justify-center">
          <Lock className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <div className="overline text-ink-soft mt-6">Admin pristup</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-ink mt-2">
          Prijava u admin panel
        </h1>
        <p className="text-sm text-ink-soft mt-3 leading-relaxed">
          Unesite svoju email adresu i lozinku.
        </p>

        <div className="mt-8 space-y-4">
          <div>
            <Label className="overline text-ink-soft">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="ime@infokarta.hr" className="mt-2 rounded-sm h-11" data-testid="login-email" />
          </div>
          <div>
            <Label className="overline text-ink-soft">Lozinka</Label>
            <div className="relative mt-2">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-sm h-11 pr-11"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Sakrij lozinku" : "Prikaži lozinku"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-ink-soft hover:text-ink transition-colors"
                data-testid="login-password-toggle"
              >
                {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.75} /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
              </button>
            </div>
          </div>
        </div>

        <button type="submit" disabled={pending} data-testid="login-submit"
          className="w-full mt-6 bg-ink text-white py-3 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors disabled:opacity-60">
          {pending ? "Prijava..." : "Prijavi se"}
        </button>
      </form>
    </div>
  );
}

/* ----------------- Applications tab (inbox: Novo + Ugovoreno) ----------------- */

function ApplicationsTab({ apps, loading, filter, setFilter, onOpen }) {
  const [query, setQuery] = useState("");
  const novoCount = apps.filter((a) => !a.is_contracted).length;
  const contractedCount = apps.filter((a) => a.is_contracted).length;
  const baseFiltered =
    filter === "novo" ? apps.filter((a) => !a.is_contracted)
    : filter === "ugovoreno" ? apps.filter((a) => a.is_contracted)
    : apps;
  const filteredApps = baseFiltered.filter((a) => matchSearch(a, query));

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
        <StatCard label="Ukupno prijava" value={apps.length} icon={Inbox} testid="kpi-total" />
        <StatCard label="Novo" value={novoCount} icon={Inbox} testid="kpi-novo" />
        <StatCard label="Ugovoreno" value={contractedCount} icon={FileCheck} testid="kpi-contracted" accent />
      </div>

      <div className="mt-8 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>Sve</FilterChip>
          <FilterChip active={filter === "novo"} onClick={() => setFilter("novo")}>Novo</FilterChip>
          <FilterChip active={filter === "ugovoreno"} onClick={() => setFilter("ugovoreno")}>Ugovoreno</FilterChip>
        </div>
        <div className="flex items-center gap-3">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Pretraži po imenu, adresi, OIB-u..."
            testid="search-prijave"
          />
          <span className="text-xs text-ink-soft whitespace-nowrap">{filteredApps.length} prijava</span>
        </div>
      </div>

      <div className="mt-6 border border-hair bg-white overflow-x-auto">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-ink-soft">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Učitavanje...
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="p-16 text-center text-ink-soft">Nema prijava za prikaz.</div>
        ) : (
          <Table data-testid="admin-table">
            <TableHeader>
              <TableRow>
                <TableHead className="overline text-ink-soft">Br. / Datum</TableHead>
                <TableHead className="overline text-ink-soft">Klijent</TableHead>
                <TableHead className="overline text-ink-soft">Kontakt</TableHead>
                <TableHead className="overline text-ink-soft">Objekt</TableHead>
                <TableHead className="overline text-ink-soft">Projektant</TableHead>
                <TableHead className="overline text-ink-soft">Stanje</TableHead>
                <TableHead className="overline text-ink-soft text-right">Akcije</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApps.map((a) => {
                const d = new Date(a.created_at);
                return (
                  <TableRow key={a.id} className="hover:bg-secondary/40" data-testid={`row-${a.id}`}>
                    <TableCell className="text-sm text-ink-soft whitespace-nowrap">
                      {a.is_contracted && a.project_number ? (
                        <div className="font-display text-lg font-bold text-[#D92D20] leading-none">
                          #{a.project_number}
                        </div>
                      ) : (
                        <div className="text-xs text-ink-soft/60">—</div>
                      )}
                      <div className="text-xs mt-1.5">{d.toLocaleDateString("hr-HR")}</div>
                      <div className="text-[10px]">{d.toLocaleTimeString("hr-HR", { hour: "2-digit", minute: "2-digit" })}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-ink">{a.full_name}</div>
                      <div className="text-xs text-ink-soft">{a.city}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="text-ink">{a.email}</div>
                      <div className="text-ink-soft text-xs">{a.phone}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="text-ink">{TYPES[a.property_type] || a.property_type}</div>
                      <div className="text-ink-soft text-xs">{a.property_address}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {a.assigned_to ? (
                        <div className="text-ink font-medium">{a.assigned_to}</div>
                      ) : (
                        <div className="text-ink-soft/60 text-xs">—</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {a.is_contracted ? (
                        <Badge className="bg-[#D92D20] text-white rounded-sm font-medium">
                          <FileCheck className="w-3 h-3 mr-1" strokeWidth={2} /> Ugovoreno
                        </Badge>
                      ) : (
                        <Badge className="bg-[#0A192F] text-white rounded-sm font-medium">Novo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => onOpen(a)}
                        className="text-sm font-medium text-ink border-b border-ink/40 hover:border-ink"
                        data-testid={`open-${a.id}`}>
                        Detalji
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  );
}

/* ----------------- Projects tab (only contracted) ----------------- */

function ProjectsTab({ apps, loading, onOpen, onOpenDoc, isOwner = false }) {
  const [pFilter, setPFilter] = useState("all");
  const [paidFilter, setPaidFilter] = useState("all"); // all | paid | unpaid (only for owner)
  const [query, setQuery] = useState("");
  const counts = {
    novo: apps.filter((a) => (a.status || "novo") === "novo").length,
    u_obradi: apps.filter((a) => a.status === "u_obradi").length,
    predan: apps.filter((a) => a.status === "predan").length,
    rijeseno: apps.filter((a) => a.status === "rijeseno").length,
    zavrsen: apps.filter((a) => a.status === "zavrsen").length,
    stranka_odustala: apps.filter((a) => a.status === "stranka_odustala").length,
    odbijeno: apps.filter((a) => a.status === "odbijeno").length,
  };
  let baseFiltered = pFilter === "all" ? apps : apps.filter((a) => (a.status || "novo") === pFilter);
  if (isOwner && paidFilter !== "all") {
    baseFiltered = baseFiltered.filter((a) => paidFilter === "paid" ? !!a.paid : !a.paid);
  }
  const searched = baseFiltered.filter((a) => matchSearch(a, query));
  const projects = [...searched].sort((a, b) => (b.project_number || 0) - (a.project_number || 0));

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
        <StatCard label="U obradi" value={counts.u_obradi} icon={Clock} testid="proj-u-obradi" />
        <StatCard label="Završen" value={counts.zavrsen} icon={ShieldCheck} testid="proj-zavrsen" />
        <StatCard label="Predan" value={counts.predan} icon={FileText} testid="proj-predan" />
        <StatCard label="Rješenje izdano" value={counts.rijeseno} icon={CheckCircle2} testid="proj-rijeseno" />
      </div>

      <div className="mt-8 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <FilterChip active={pFilter === "all"} onClick={() => setPFilter("all")}>Sve</FilterChip>
          {STATUSES.map((s) => (
            <FilterChip key={s.value} active={pFilter === s.value} onClick={() => setPFilter(s.value)}>
              {s.label}
            </FilterChip>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="Pretraži po broju, projektantu, adresi..."
            testid="search-projekti"
          />
          <span className="text-xs text-ink-soft whitespace-nowrap">{projects.length} projekata</span>
        </div>
      </div>

      {isOwner && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="paid-filter-bar">
          <span className="overline text-ink-soft mr-1">Plaćanje:</span>
          <FilterChip active={paidFilter === "all"} onClick={() => setPaidFilter("all")} testid="paid-filter-all">Sve</FilterChip>
          <FilterChip active={paidFilter === "paid"} onClick={() => setPaidFilter("paid")} testid="paid-filter-paid">Plaćeno</FilterChip>
          <FilterChip active={paidFilter === "unpaid"} onClick={() => setPaidFilter("unpaid")} testid="paid-filter-unpaid">Nije plaćeno</FilterChip>
        </div>
      )}

      <div className="mt-6 border border-hair bg-white overflow-x-auto">

        {loading ? (
          <div className="p-16 flex items-center justify-center text-ink-soft">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Učitavanje...
          </div>
        ) : projects.length === 0 ? (
          <div className="p-16 text-center text-ink-soft">
            {apps.length === 0
              ? <>Još nema ugovorenih projekata.<br /><span className="text-xs">Označite prijavu kao ugovorenu da postane projekt.</span></>
              : "Nema projekata za odabrani filter."}
          </div>
        ) : (
          <Table data-testid="projects-table">
            <TableHeader>
              <TableRow>
                <TableHead className="overline text-ink-soft">Br. projekta</TableHead>
                <TableHead className="overline text-ink-soft">Klijent</TableHead>
                <TableHead className="overline text-ink-soft">Objekt</TableHead>
                <TableHead className="overline text-ink-soft">Projektant</TableHead>
                <TableHead className="overline text-ink-soft">Status</TableHead>
                {isOwner && <TableHead className="overline text-ink-soft text-right">Cijena</TableHead>}
                {isOwner && <TableHead className="overline text-ink-soft">Plaćeno</TableHead>}
                <TableHead className="overline text-ink-soft">Ugovoreno</TableHead>
                <TableHead className="overline text-ink-soft text-right">Dokumenti</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((a) => {
                const s = statusMap[a.status] || statusMap.novo;
                return (
                  <TableRow key={a.id} className="hover:bg-secondary/40" data-testid={`project-row-${a.id}`}>
                    <TableCell className="whitespace-nowrap">
                      <div className="font-display text-2xl font-bold text-[#D92D20] leading-none">
                        #{a.project_number}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-ink">{a.full_name}</div>
                      <div className="text-xs text-ink-soft">{a.email} · {a.phone}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="text-ink">{TYPES[a.property_type] || a.property_type}</div>
                      <div className="text-ink-soft text-xs">{a.property_address}, {a.city}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {a.assigned_to ? (
                        <div className="text-ink font-medium">{a.assigned_to}</div>
                      ) : (
                        <div className="text-[#D92D20] text-xs">⚠ nije dodijeljen</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${s.tone} rounded-sm font-medium`}>{s.label}</Badge>
                    </TableCell>
                    {isOwner && (
                      <TableCell className="text-sm text-right whitespace-nowrap font-medium text-ink" data-testid={`project-price-${a.id}`}>
                        {typeof a.price === "number"
                          ? `${a.price.toLocaleString("hr-HR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                          : <span className="text-ink-soft">—</span>}
                      </TableCell>
                    )}
                    {isOwner && (
                      <TableCell data-testid={`project-paid-${a.id}`}>
                        {a.paid ? (
                          <Badge className="bg-[#16A34A] text-white rounded-sm">Plaćeno</Badge>
                        ) : (
                          <Badge className="bg-[#D92D20]/15 text-[#D92D20] border border-[#D92D20]/30 rounded-sm">Nije</Badge>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-sm text-ink-soft whitespace-nowrap">
                      {a.contracted_at ? new Date(a.contracted_at).toLocaleDateString("hr-HR") : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => onOpenDoc(a.id, "narudzbenica")}
                          title="Narudžbenica"
                          data-testid={`doc-nar-${a.id}`}
                          className="p-2 border border-hair hover:border-ink rounded-sm text-ink"
                        >
                          <FileText className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => onOpenDoc(a.id, "punomoc")}
                          title="Punomoć"
                          data-testid={`doc-pun-${a.id}`}
                          className="p-2 border border-hair hover:border-ink rounded-sm text-ink"
                        >
                          <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => onOpenDoc(a.id, "dof-zahtjev")}
                          title="Zahtjev za DOF uvjerenje"
                          data-testid={`doc-dof-${a.id}`}
                          className="p-2 border border-hair hover:border-ink rounded-sm text-ink"
                        >
                          <FileCheck className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => onOpen(a)}
                          data-testid={`open-project-${a.id}`}
                          className="px-3 py-2 bg-ink text-white text-xs font-medium rounded-sm hover:bg-[#112240]"
                        >
                          Detalji
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  );
}

/* ----------------- Users tab ----------------- */

function UsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: "", password: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/auth/users`, { headers: authHeaders() });
      setUsers(data);
    } catch (err) {
      toast.error(formatError(err, "Greška pri dohvaćanju korisnika."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.full_name) return;
    setSaving(true);
    try {
      await axios.post(`${API}/auth/users`, form, { headers: authHeaders() });
      toast.success("Korisnik dodan.");
      setOpen(false);
      setForm({ full_name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatError(err, "Dodavanje nije uspjelo."));
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (u.id === currentUser.id) return;
    if (!window.confirm(`Obrisati korisnika ${u.full_name}?`)) return;
    try {
      await axios.delete(`${API}/auth/users/${u.id}`, { headers: authHeaders() });
      toast.success("Korisnik obrisan.");
      load();
    } catch (err) { toast.error(formatError(err, "Brisanje nije uspjelo.")); }
  };

  const startEdit = (u) => {
    setEditUser(u);
    setEditForm({ full_name: u.full_name, password: "" });
  };

  const saveEditUser = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    const payload = {};
    if (editForm.full_name && editForm.full_name !== editUser.full_name) payload.full_name = editForm.full_name;
    if (editForm.password) payload.password = editForm.password;
    if (Object.keys(payload).length === 0) { setEditUser(null); return; }
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/auth/users/${editUser.id}`, payload, { headers: authHeaders() });
      toast.success("Korisnik ažuriran.");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(formatError(err, "Ažuriranje nije uspjelo."));
    } finally { setSavingEdit(false); }
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-ink-soft">{users.length} korisnika</div>
        <button onClick={() => setOpen(true)} data-testid="add-user-btn"
          className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#B91C1C]">
          <UserPlus className="w-4 h-4" strokeWidth={1.5} /> Dodaj korisnika
        </button>
      </div>

      <div className="border border-hair bg-white">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-ink-soft">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Učitavanje...
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="overline text-ink-soft">Ime i prezime</TableHead>
                <TableHead className="overline text-ink-soft">Email</TableHead>
                <TableHead className="overline text-ink-soft">Uloga</TableHead>
                <TableHead className="overline text-ink-soft">Dodan</TableHead>
                <TableHead className="overline text-ink-soft text-right">Akcije</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="hover:bg-secondary/40" data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="font-medium text-ink">{u.full_name}</div>
                    {u.id === currentUser.id && <div className="text-xs text-[#D92D20]">vi</div>}
                  </TableCell>
                  <TableCell className="text-sm text-ink-soft">
                    <a href={`mailto:${u.email}`} className="inline-flex items-center gap-1.5 hover:text-ink">
                      <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> {u.email}
                    </a>
                  </TableCell>
                  <TableCell><Badge className="bg-ink text-white rounded-sm">{u.role}</Badge></TableCell>
                  <TableCell className="text-sm text-ink-soft">
                    {new Date(u.created_at).toLocaleDateString("hr-HR")}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => startEdit(u)}
                        className="inline-flex items-center gap-1.5 text-sm text-ink hover:underline"
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /> Uredi
                      </button>
                      <button onClick={() => remove(u)} disabled={u.id === currentUser.id}
                        className="inline-flex items-center gap-1.5 text-sm text-[#D92D20] hover:underline disabled:opacity-30 disabled:no-underline"
                        data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Obriši
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="add-user-dialog">
          <form onSubmit={create}>
            <DialogHeader>
              <div className="overline text-ink-soft">Novi korisnik</div>
              <DialogTitle className="font-display text-2xl text-ink">Dodaj člana tima</DialogTitle>
              <DialogDescription className="text-ink-soft">
                Novi korisnik može se odmah prijaviti s dolje zadanim podacima.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="overline text-ink-soft">Ime i prezime</Label>
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="mt-2 rounded-sm h-11" placeholder="Vlado Krstulović" data-testid="new-user-name" />
              </div>
              <div>
                <Label className="overline text-ink-soft">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 rounded-sm h-11" placeholder="ime@infokarta.hr" data-testid="new-user-email" />
              </div>
              <div>
                <Label className="overline text-ink-soft">Početna lozinka</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-2 rounded-sm h-11" placeholder="min. 6 znakova" data-testid="new-user-password" />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <button type="button" onClick={() => setOpen(false)}
                className="border border-hair text-ink px-5 py-2.5 text-sm rounded-sm hover:bg-secondary">
                Odustani
              </button>
              <button type="submit" disabled={saving} data-testid="new-user-submit"
                className="bg-ink text-white px-5 py-2.5 text-sm rounded-sm hover:bg-[#112240] disabled:opacity-60">
                {saving ? "Spremam..." : "Dodaj korisnika"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="edit-user-dialog">
          <form onSubmit={saveEditUser}>
            <DialogHeader>
              <div className="overline text-ink-soft">Uređivanje korisnika</div>
              <DialogTitle className="font-display text-2xl text-ink">{editUser?.full_name}</DialogTitle>
              <DialogDescription className="text-ink-soft">
                Email se ne može mijenjati. Lozinku unesite samo ako je želite promijeniti.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="overline text-ink-soft">Ime i prezime</Label>
                <Input
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  className="mt-2 rounded-sm h-11"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label className="overline text-ink-soft">Email</Label>
                <Input value={editUser?.email || ""} disabled className="mt-2 rounded-sm h-11 bg-secondary/40" />
              </div>
              <div>
                <Label className="overline text-ink-soft">Nova lozinka (opcionalno)</Label>
                <Input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  className="mt-2 rounded-sm h-11"
                  placeholder="Ostavite prazno za zadržavanje stare lozinke"
                  data-testid="edit-user-password"
                />
              </div>
            </div>
            <DialogFooter className="mt-6 gap-2">
              <button type="button" onClick={() => setEditUser(null)}
                className="border border-hair text-ink px-5 py-2.5 text-sm rounded-sm hover:bg-secondary">
                Odustani
              </button>
              <button type="submit" disabled={savingEdit} data-testid="edit-user-submit"
                className="bg-[#D92D20] text-white px-5 py-2.5 text-sm rounded-sm hover:bg-[#B91C1C] disabled:opacity-60">
                {savingEdit ? "Spremam..." : "Spremi izmjene"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/* ----------------- Helpers ----------------- */

function StatCard({ label, value, icon: Icon, testid, accent }) {
  return (
    <div className={`p-5 ${accent ? "bg-[#FFF5F4]" : "bg-white"}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className={`overline ${accent ? "text-[#D92D20]" : "text-ink-soft"}`}>{label}</span>
        <Icon className={`w-4 h-4 ${accent ? "text-[#D92D20]" : "text-ink-soft"}`} strokeWidth={1.5} />
      </div>
      <div className={`font-display text-3xl tracking-tight font-bold mt-2 ${accent ? "text-[#D92D20]" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ${
        active ? "bg-ink text-white border-ink" : "bg-white text-ink-soft border-hair hover:text-ink"
      }`}>
      {children}
    </button>
  );
}

function DetailsDialog({ application, onClose, onStatusChange, onSaveNote, onMarkContracted, onAssign, onOpenDoc, onSaveEdit, isOwner = false }) {
  const [note, setNote] = useState("");
  const [assigned, setAssigned] = useState("");
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  useEffect(() => {
    setNote(application?.note || "");
    setAssigned(application?.assigned_to || "");
    setEditing(false);
    setEditForm(application ? { ...application } : null);
  }, [application]);
  useEffect(() => {
    if (!application) return;
    axios.get(`${API}/auth/users`, { headers: authHeaders() })
      .then((r) => setUsers(r.data))
      .catch(() => {});
  }, [application]);
  if (!application) return null;
  const s = statusMap[application.status] || statusMap.novo;
  const isContracted = !!application.is_contracted;

  const cancelEdit = () => {
    setEditForm({ ...application });
    setEditing(false);
  };
  const saveEdit = async () => {
    if (!editForm) return;
    setSavingEdit(true);
    const payload = {};
    const fields = ["full_name","email","phone","client_address","client_oib","property_address","city","property_type","year_built","area_m2","cadastral_municipality","cadastral_parcel","description"];
    fields.forEach((k) => {
      if ((editForm[k] ?? "") !== (application[k] ?? "")) payload[k] = editForm[k] || null;
    });
    if (isOwner) {
      // Cijena
      const oldP = application.price ?? null;
      const newPRaw = editForm.price;
      const newP = (newPRaw === "" || newPRaw === null || newPRaw === undefined) ? null : Number(newPRaw);
      if (newP !== oldP && !(newP === null && oldP === null)) {
        if (newP !== null && Number.isNaN(newP)) {
          toast.error("Cijena mora biti broj.");
          setSavingEdit(false); return;
        }
        payload.price = newP;
      }
      // Plaćeno
      const oldPaid = !!application.paid;
      const newPaid = !!editForm.paid;
      if (oldPaid !== newPaid) payload.paid = newPaid;
    }
    if (Object.keys(payload).length === 0) { setEditing(false); setSavingEdit(false); return; }
    try {
      await onSaveEdit(application.id, payload);
      setEditing(false);
    } finally { setSavingEdit(false); }
  };
  const upd = (k) => (e) => setEditForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <Dialog open={!!application} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="admin-details-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="overline text-ink-soft">Prijava #{application.id.slice(0, 8)}</div>
              {isContracted && application.project_number && (
                <Badge className="bg-[#D92D20] text-white rounded-sm">
                  <FileCheck className="w-3 h-3 mr-1" strokeWidth={2} /> Projekt #{application.project_number}
                </Badge>
              )}
            </div>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                data-testid="edit-toggle"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-ink border border-hair hover:border-ink rounded-sm px-3 py-1.5"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /> Uredi podatke
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={cancelEdit} className="text-sm border border-hair text-ink px-3 py-1.5 rounded-sm hover:bg-secondary">Odustani</button>
                <button onClick={saveEdit} disabled={savingEdit} data-testid="edit-save"
                  className="text-sm bg-[#D92D20] text-white px-3 py-1.5 rounded-sm hover:bg-[#B91C1C] disabled:opacity-60">
                  {savingEdit ? "Spremam..." : "Spremi izmjene"}
                </button>
              </div>
            )}
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight text-ink">
            {editing ? "Uređivanje prijave" : application.full_name}
          </DialogTitle>
          <DialogDescription className="text-ink-soft">
            Zaprimljeno {new Date(application.created_at).toLocaleString("hr-HR")}
            {application.contracted_at && ` · Ugovoreno ${new Date(application.contracted_at).toLocaleDateString("hr-HR")}`}
          </DialogDescription>
        </DialogHeader>

        {!editing ? (
          <>
            <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
              <Info label="Ime i prezime" value={application.full_name} />
              <Info label="E-mail" value={application.email} />
              <Info label="Telefon" value={application.phone} />
              <Info label="OIB stranke" value={application.client_oib || "—"} />
              <Info label="Adresa stanovanja stranke" value={application.client_address || "—"} />
              <Info label="Mjesto" value={application.city} />
              <Info label="Adresa objekta" value={application.property_address} />
              <Info label="Vrsta objekta" value={TYPES[application.property_type] || application.property_type} />
              <Info label="Godina izgradnje" value={application.year_built || "—"} />
              <Info label="Površina" value={application.area_m2 ? `${application.area_m2} m²` : "—"} />
              <Info label="K.O. (katastarska općina)" value={application.cadastral_municipality || application.cadastral_info || "—"} />
              <Info label="K.Č. (katastarska čestica)" value={application.cadastral_parcel || "—"} />
            </div>
            <div className="mt-2">
              <div className="overline text-ink-soft">Opis</div>
              <p className="text-sm text-ink mt-2 leading-relaxed whitespace-pre-wrap border border-hair p-3 bg-secondary/40">
                {application.description}
              </p>
            </div>
            {isOwner && (
              <div className="mt-4 border border-[#0A192F]/20 bg-[#FFF8E1] p-4 -mx-1" data-testid="owner-financials-view">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-3.5 h-3.5 text-ink" strokeWidth={1.75} />
                  <div className="overline text-ink">Interno — vidi samo vlasnik</div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Info
                    label="Cijena radova"
                    value={typeof application.price === "number" ? `${application.price.toLocaleString("hr-HR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "—"}
                  />
                  <div>
                    <div className="overline text-ink-soft">Plaćeno</div>
                    <div className="mt-1.5 text-ink font-medium">
                      {application.paid ? (
                        <Badge className="bg-[#16A34A] text-white rounded-sm">Plaćeno</Badge>
                      ) : (
                        <Badge className="bg-[#D92D20] text-white rounded-sm">Nije plaćeno</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="border border-[#D92D20] bg-[#FFF5F4] p-4 -mx-1 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <EditField label="Ime i prezime" value={editForm.full_name} onChange={upd("full_name")} />
              <EditField label="E-mail" type="email" value={editForm.email} onChange={upd("email")} />
              <EditField label="Telefon" value={editForm.phone} onChange={upd("phone")} />
              <EditField label="OIB stranke" value={editForm.client_oib || ""} onChange={upd("client_oib")} />
              <EditField label="Adresa stanovanja stranke" value={editForm.client_address || ""} onChange={upd("client_address")} />
              <EditField label="Mjesto" value={editForm.city} onChange={upd("city")} />
              <EditField label="Adresa objekta" value={editForm.property_address} onChange={upd("property_address")} />
              <div>
                <Label className="overline text-ink-soft">Vrsta objekta</Label>
                <Select value={editForm.property_type || undefined} onValueChange={upd("property_type")}>
                  <SelectTrigger className="rounded-sm mt-2 h-10 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <EditField label="Godina izgradnje" value={editForm.year_built || ""} onChange={upd("year_built")} />
              <EditField label="Površina (m²)" value={editForm.area_m2 || ""} onChange={upd("area_m2")} />
              <EditField label="K.O. (katastarska općina)" value={editForm.cadastral_municipality || ""} onChange={upd("cadastral_municipality")} />
              <EditField label="K.Č. (katastarska čestica)" value={editForm.cadastral_parcel || ""} onChange={upd("cadastral_parcel")} />
            </div>
            <div className="mt-3">
              <Label className="overline text-ink-soft">Opis</Label>
              <Textarea value={editForm.description || ""} onChange={upd("description")} rows={4} className="mt-2 rounded-sm bg-white" />
            </div>
            {isOwner && (
              <div className="mt-4 border-t border-[#D92D20]/30 pt-4" data-testid="owner-financials-edit">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-3.5 h-3.5 text-ink" strokeWidth={1.75} />
                  <div className="overline text-ink">Interno — vidi samo vlasnik</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="overline text-ink-soft">Cijena radova (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.price ?? ""}
                      onChange={upd("price")}
                      placeholder="npr. 1500.00"
                      className="mt-2 rounded-sm h-10 bg-white"
                      data-testid="edit-price"
                    />
                  </div>
                  <div>
                    <Label className="overline text-ink-soft">Status plaćanja</Label>
                    <Select
                      value={editForm.paid ? "paid" : "unpaid"}
                      onValueChange={(v) => setEditForm((f) => ({ ...f, paid: v === "paid" }))}
                    >
                      <SelectTrigger className="rounded-sm mt-2 h-10 bg-white" data-testid="edit-paid">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unpaid">Nije plaćeno</SelectItem>
                        <SelectItem value="paid">Plaćeno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CONTRACTING BLOCK */}
        <div className={`mt-2 border p-4 ${isContracted ? "border-[#D92D20] bg-[#FFF5F4]" : "border-hair bg-secondary/30"}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="overline text-ink-soft">Ugovaranje projekta</div>
              <div className="text-sm text-ink mt-1">
                {isContracted
                  ? `Projekt je ugovoren — interni broj #${application.project_number}.`
                  : "Označite kao ugovoreno kad klijent prihvati ponudu. Sustav automatski dodjeljuje sljedeći broj projekta."}
              </div>
            </div>
            {!isContracted && isOwner && (
              <button
                onClick={() => onMarkContracted(application.id)}
                data-testid="mark-contracted"
                className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#B91C1C] whitespace-nowrap"
              >
                <FileCheck className="w-4 h-4" strokeWidth={1.75} /> Označi kao ugovoreno
              </button>
            )}
            {!isContracted && !isOwner && (
              <span className="text-xs text-ink-soft italic">Samo vlasnik može ugovarati projekte.</span>
            )}
          </div>

          {isContracted && (
            <>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                <div>
                  <Label className="overline text-ink-soft">Projektant (zadužen)</Label>
                  {isOwner ? (
                    <Select value={assigned || undefined} onValueChange={(v) => { setAssigned(v); onAssign(application.id, v); }}>
                      <SelectTrigger className="rounded-sm mt-2 h-11 bg-white" data-testid="assign-select">
                        <SelectValue placeholder="Odaberi projektanta" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.full_name}>{u.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-2 h-11 px-3 flex items-center bg-secondary/40 border border-hair rounded-sm text-sm text-ink" data-testid="assign-readonly">
                      {assigned || <span className="text-ink-soft">Nije zadužen projektant</span>}
                    </div>
                  )}
                </div>
                <div className="text-xs text-ink-soft sm:text-right">
                  {isOwner
                    ? (assigned ? `Zadužen: ${assigned}` : "Nije zadužen projektant")
                    : "Samo vlasnik može mijenjati zaduženog projektanta."}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => onOpenDoc(application.id, "narudzbenica")}
                  data-testid="open-narudzbenica"
                  className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2.5 text-sm font-medium rounded-sm hover:bg-[#112240]"
                >
                  <FileText className="w-4 h-4" strokeWidth={1.5} /> Narudžbenica
                  <ExternalLink className="w-3 h-3 opacity-70" strokeWidth={1.75} />
                </button>
                <button
                  onClick={() => onOpenDoc(application.id, "punomoc")}
                  data-testid="open-punomoc"
                  className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2.5 text-sm font-medium rounded-sm hover:bg-[#112240]"
                >
                  <ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Punomoć za zastupanje
                  <ExternalLink className="w-3 h-3 opacity-70" strokeWidth={1.75} />
                </button>
                <button
                  onClick={() => onOpenDoc(application.id, "dof-zahtjev")}
                  data-testid="open-dof"
                  className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2.5 text-sm font-medium rounded-sm hover:bg-[#112240]"
                >
                  <FileCheck className="w-4 h-4" strokeWidth={1.5} /> Zahtjev za DOF uvjerenje
                  <ExternalLink className="w-3 h-3 opacity-70" strokeWidth={1.75} />
                </button>
              </div>
            </>
          )}
        </div>

        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="overline text-ink-soft">Status</Label>
            <Select value={application.status} onValueChange={(v) => onStatusChange(application.id, v)}>
              <SelectTrigger className="rounded-sm mt-2 h-11" data-testid="dialog-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((st) => <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-2"><Badge className={`${s.tone} rounded-sm`}>{s.label}</Badge></div>
          </div>
          <div>
            <Label className="overline text-ink-soft">Bilješka</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4}
              className="mt-2 rounded-sm" placeholder="Interna bilješka..." data-testid="dialog-note" />
          </div>
        </div>

        {/* ATTACHMENTS */}
        <AttachmentsSection application={application} />

        <DialogFooter className="mt-2">
          <button onClick={onClose}
            className="border border-hair text-ink px-5 py-2.5 text-sm rounded-sm hover:bg-secondary">
            Zatvori
          </button>
          <button onClick={() => onSaveNote(application.id, note)}
            className="bg-ink text-white px-5 py-2.5 text-sm rounded-sm hover:bg-[#112240]"
            data-testid="dialog-save-note">
            Spremi bilješku
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="overline text-ink-soft">{label}</div>
      <div className="text-ink text-sm mt-1">{value}</div>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="overline text-ink-soft">{label}</Label>
      <Input type={type} value={value || ""} onChange={onChange} className="mt-2 rounded-sm h-10 bg-white" />
    </div>
  );
}

/* ----------------- Attachments ----------------- */

function fmtBytes(n) {
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / (1024 * 1024)).toFixed(2) + " MB";
}

function AttachmentsSection({ application }) {
  const [items, setItems] = useState(application?.attachments || []);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setItems(application?.attachments || []); }, [application]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          const { data } = await axios.post(
            `${API}/applications/${application.id}/attachments`,
            fd,
            { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } }
          );
          uploaded.push(data);
        } catch (err) {
          toast.error(formatError(err, `Upload nije uspio: ${f.name}`));
        }
      }
      if (uploaded.length) {
        setItems((prev) => [...prev, ...uploaded]);
        toast.success(`Dodano ${uploaded.length} datoteka.`);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const openFile = async (att) => {
    try {
      const { data } = await axios.get(
        `${API}/applications/${application.id}/attachments/${att.id}`,
        { headers: authHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error(formatError(err, "Otvaranje nije uspjelo."));
    }
  };

  const removeFile = async (att) => {
    if (!window.confirm(`Obrisati datoteku ${att.filename}?`)) return;
    try {
      await axios.delete(`${API}/applications/${application.id}/attachments/${att.id}`, { headers: authHeaders() });
      setItems((prev) => prev.filter((x) => x.id !== att.id));
      toast.success("Datoteka obrisana.");
    } catch (err) { toast.error(formatError(err, "Brisanje nije uspjelo.")); }
  };

  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between">
        <Label className="overline text-ink-soft flex items-center gap-2">
          <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} /> Privici uz bilješku
        </Label>
        <span className="text-xs text-ink-soft">{items.length} datoteka</span>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(Array.from(e.dataTransfer.files || [])); }}
        className={`mt-2 border-2 border-dashed rounded-sm p-5 text-center transition-colors ${
          drag ? "border-[#D92D20] bg-[#FFF5F4]" : "border-hair bg-secondary/30"
        }`}
        data-testid="attachments-dropzone"
      >
        <Upload className="w-6 h-6 mx-auto text-ink-soft" strokeWidth={1.5} />
        <div className="text-sm text-ink mt-2">
          Povucite datoteke ovdje ili{" "}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-[#D92D20] font-medium border-b border-[#D92D20]/40 hover:border-[#D92D20]"
            data-testid="attachments-browse"
          >
            odaberite
          </button>
        </div>
        <div className="text-xs text-ink-soft mt-1">JPG, PNG, WEBP, GIF ili PDF · max 10 MB po datoteci</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
          className="hidden"
          onChange={(e) => upload(Array.from(e.target.files || []))}
        />
        {uploading && (
          <div className="text-xs text-ink-soft mt-2 inline-flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" /> Učitavanje...
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((a) => {
            const isImg = (a.content_type || "").startsWith("image/");
            return (
              <div key={a.id} className="flex items-center gap-3 border border-hair bg-white p-2 pr-3" data-testid={`att-${a.id}`}>
                <button
                  onClick={() => openFile(a)}
                  className="w-10 h-10 shrink-0 bg-secondary border border-hair flex items-center justify-center text-ink"
                  title="Otvori"
                >
                  {isImg ? <ImageIcon className="w-4 h-4" strokeWidth={1.5} /> : <FileText className="w-4 h-4" strokeWidth={1.5} />}
                </button>
                <div className="min-w-0 flex-1">
                  <button onClick={() => openFile(a)} className="block text-sm font-medium text-ink truncate hover:underline text-left w-full">
                    {a.filename}
                  </button>
                  <div className="text-[11px] text-ink-soft">
                    {fmtBytes(a.size)} · {a.uploaded_by || "—"}
                  </div>
                </div>
                <button onClick={() => openFile(a)} className="p-1.5 text-ink-soft hover:text-ink" title="Otvori"><Download className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                <button onClick={() => removeFile(a)} className="p-1.5 text-ink-soft hover:text-[#D92D20]" title="Obriši" data-testid={`att-del-${a.id}`}><X className="w-3.5 h-3.5" strokeWidth={1.75} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
